import { useEffect } from 'react';
import { useLanguage } from '../i18n/LanguageContext';

const BASE_URL = 'https://plexterra.co';
const SITE_NAME = 'PLEXTERRA';
const DEFAULT_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/87a29eb6-aefe-4729-b4ce-df15eb744e6e/images/1d13c84e539289423282f770af452b4fe3de35124c508b73ae7617182c78936a.png';

const DEFAULT_META = {
  fr: {
    title: 'PLEXTERRA — Développement immobilier | Canada & USA',
    description: 'PLEXTERRA — Firme de développement immobilier. Analyse stratégique, faisabilité, coordination et services techniques pour investisseurs, promoteurs et propriétaires au Canada et aux États-Unis.',
    keywords: 'développement immobilier, analyse de terrain, étude de faisabilité, inspection bâtiment, inspection multilogement, loi 16, fonds de prévoyance, plans architecturaux, coordination architecturale, ingénierie, gestion de projet, promoteur immobilier, investisseur immobilier, Québec, Canada'
  },
  en: {
    title: 'PLEXTERRA — Real Estate Development | Canada & USA',
    description: 'PLEXTERRA — Real estate development firm. Strategic analysis, feasibility, coordination and technical services for investors, developers and landowners across Canada and the USA.',
    keywords: 'real estate development, land analysis, feasibility study, property due diligence, building inspection, architectural design, project management, real estate developer, Canada, USA'
  }
};

// Attribute-based upsert of meta/link/script tags in <head>
const setMetaTag = (attr, key, value, extras = {}) => {
  if (!value) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute('data-plexterra-seo', '1');
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
  Object.entries(extras).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};

const setLinkTag = (rel, href) => {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute('data-plexterra-seo', '1');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
};

const setJsonLd = (id, data) => {
  const existing = document.head.querySelector(`script[data-jsonld-id="${id}"]`);
  if (existing) existing.remove();
  if (!data) return null;
  const script = document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.setAttribute('data-jsonld-id', id);
  script.setAttribute('data-plexterra-seo', '1');
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
  return script;
};

const removeIfExists = (selector) => {
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
};

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  product = null,
  article = null,
  faq = null
}) => {
  const { language } = useLanguage();

  useEffect(() => {
    const lang = language || 'fr';
    const meta = DEFAULT_META[lang] || DEFAULT_META.fr;

    const pageTitle = title
      ? (title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`)
      : meta.title;
    const pageDescription = description || meta.description;
    const pageKeywords = keywords || meta.keywords;
    const rawImage = image || DEFAULT_IMAGE;
    const pageImage = rawImage.startsWith('http') ? rawImage : `${BASE_URL}${rawImage}`;
    const pageUrl = url ? `${BASE_URL}${url}` : BASE_URL;

    // 1. Title & html lang
    document.title = pageTitle;
    document.documentElement.setAttribute('lang', lang);

    // 2. Basic meta
    setMetaTag('name', 'description', pageDescription);
    setMetaTag('name', 'keywords', pageKeywords);
    setMetaTag('name', 'author', 'PLEXTERRA');
    setMetaTag('name', 'robots', 'index, follow');
    setLinkTag('canonical', pageUrl);

    // 3. Open Graph
    setMetaTag('property', 'og:type', article ? 'article' : type);
    setMetaTag('property', 'og:url', pageUrl);
    setMetaTag('property', 'og:title', pageTitle);
    setMetaTag('property', 'og:description', pageDescription);
    setMetaTag('property', 'og:image', pageImage);
    setMetaTag('property', 'og:image:width', '1200');
    setMetaTag('property', 'og:image:height', '630');
    setMetaTag('property', 'og:image:alt', pageTitle);
    setMetaTag('property', 'og:image:secure_url', pageImage);
    setMetaTag('property', 'og:site_name', SITE_NAME);
    setMetaTag('property', 'og:locale', lang === 'fr' ? 'fr_CA' : 'en_CA');

    // 4. Twitter
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:url', pageUrl);
    setMetaTag('name', 'twitter:title', pageTitle);
    setMetaTag('name', 'twitter:description', pageDescription);
    setMetaTag('name', 'twitter:image', pageImage);
    setMetaTag('name', 'twitter:image:alt', pageTitle);
    setMetaTag('name', 'twitter:site', '@plexterra');
    setMetaTag('name', 'twitter:creator', '@plexterra');

    // 5. Geo
    setMetaTag('name', 'geo.region', 'CA-QC');
    setMetaTag('name', 'geo.placename', 'Quebec');

    // 6. Article-specific tags
    if (article) {
      setMetaTag('property', 'article:published_time', article.datePublished);
      setMetaTag('property', 'article:modified_time', article.dateModified || article.datePublished);
      setMetaTag('property', 'article:author', 'PLEXTERRA');
      setMetaTag('property', 'article:section', article.category || '');
      setMetaTag('name', 'twitter:label1', lang === 'fr' ? 'Temps de lecture' : 'Reading time');
      setMetaTag('name', 'twitter:data1', `${article.readTime || 8} min`);
      setMetaTag('name', 'twitter:label2', lang === 'fr' ? 'Catégorie' : 'Category');
      setMetaTag('name', 'twitter:data2', article.category || '');
    } else {
      // Clean up article-only meta
      removeIfExists('meta[property^="article:"]');
      removeIfExists('meta[name="twitter:label1"], meta[name="twitter:data1"], meta[name="twitter:label2"], meta[name="twitter:data2"]');
    }

    // 7. JSON-LD Organization (always present)
    setJsonLd('organization', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'PLEXTERRA',
      url: BASE_URL,
      logo: `${BASE_URL}/logo.png`,
      description: lang === 'fr'
        ? "Firme de développement immobilier : analyse stratégique, faisabilité, coordination et services techniques."
        : 'Développement immobilier firm: strategic analysis, feasibility, coordination and technical services.',
      areaServed: ['Canada', 'United States'],
      knowsLanguage: ['French', 'English']
    });

    // 8. JSON-LD Product
    if (product) {
      setJsonLd('product', {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: product.description,
        image: product.image,
        offers: {
          '@type': 'Offer',
          price: product.price,
          priceCurrency: 'CAD',
          availability: 'https://schema.org/InStock'
        },
        brand: { '@type': 'Brand', name: 'PLEXTERRA' }
      });
    } else {
      setJsonLd('product', null);
    }

    // 9. JSON-LD Article + Breadcrumb
    if (article) {
      const authorNode = article.author
        ? {
            '@type': 'Person',
            name: article.author,
            jobTitle: article.authorRole || undefined,
            image: article.authorImage ? `${BASE_URL}${article.authorImage}` : undefined,
            worksFor: { '@type': 'Organization', name: 'PLEXTERRA', url: BASE_URL }
          }
        : { '@type': 'Organization', name: 'PLEXTERRA', url: BASE_URL };

      setJsonLd('article', {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.image || pageImage,
        datePublished: article.datePublished,
        dateModified: article.dateModified || article.datePublished,
        author: authorNode,
        publisher: {
          '@type': 'Organization',
          name: 'PLEXTERRA',
          logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo.png` }
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
        articleSection: article.category,
        wordCount: article.wordCount || 1500,
        timeRequired: `PT${article.readTime || 8}M`,
        inLanguage: lang === 'fr' ? 'fr-CA' : 'en-CA'
      });

      setJsonLd('breadcrumb', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: lang === 'fr' ? 'Accueil' : 'Home', item: BASE_URL },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${BASE_URL}/blog` },
          { '@type': 'ListItem', position: 3, name: article.title, item: pageUrl }
        ]
      });
    } else {
      setJsonLd('article', null);
      setJsonLd('breadcrumb', null);
    }

    // 10. JSON-LD FAQPage — injected when the caller passes a `faq` array of
    // `{ question, answer }` objects. Enables Google FAQ rich results.
    if (Array.isArray(faq) && faq.length > 0) {
      setJsonLd('faq', {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer
          }
        }))
      });
    } else {
      setJsonLd('faq', null);
    }
  }, [language, title, description, keywords, image, url, type, product, article, faq]);

  return null;
};

export default SEO;
